import { AlertCircle } from "lucide-react";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import type { CreditsState } from "@/lib/plan-editor";

/**
 * Phase 25 — Plan Editor Credits section.
 *
 * Dumb controlled component. Ports the Credits tab from the retired
 * popup editor (deleted in Plan 25-06).
 *
 * Renders 2 number inputs — Conference Room hours/month + Meeting Room
 * hours/month. The parent translates these into plan_entitlements rows
 * keyed by an inventory item name-match at save time (Plan 25-04).
 *
 * Plan 25-04 — adds onSave + saving + missingItems prop:
 *  - missingItems=true → renders a soft-warning Alert at the top of the section
 *    explaining that Conference/Meeting Room synthetic items are absent from
 *    this space's inventory and that the credit quotas will not persist.
 *
 * Covers: ACCORD25-01 field surface (Credits), SAVE25-04 (save button).
 */

export interface CreditsSectionProps {
  value: CreditsState;
  onChange: (next: CreditsState) => void;
  /** Plan 25-04 — Save & Continue button click handler. */
  onSave: () => void | Promise<void>;
  /** Plan 25-04 — true while any section save is in flight. */
  saving: boolean;
  /**
   * Plan 25-04 — true when this space lacks either the "Conference Room"
   * or "Meeting Room" synthetic inventory item. Credit quotas cannot be
   * stored without them.
   */
  missingItems: boolean;
  /** 2026-06-09 — locks all inputs when true */
  disabled?: boolean;
  /** 2026-06-09 — Edit button handler */
  onEdit?: () => void;
}

export function CreditsSection({
  value,
  onChange,
  onSave,
  saving,
  missingItems,
  disabled = false,
  onEdit,
}: CreditsSectionProps) {
  function patch(next: Partial<CreditsState>) {
    onChange({ ...value, ...next });
  }

  return (
    <div className="space-y-4 p-1" data-testid="plan-editor-credits-section">
    <fieldset disabled={disabled} className="space-y-4 disabled:opacity-70">
      {missingItems && (
        <Alert variant="default" data-testid="plan-editor-credits-missing-items">
          <AlertCircle className="h-4 w-4" />
          <AlertTitle>Synthetic items missing</AlertTitle>
          <AlertDescription>
            This space doesn&apos;t have &ldquo;Conference Room Hours&rdquo; or
            &ldquo;Meeting Room Hours&rdquo; inventory items. Add them under
            Inventory → Items so credit quotas can be saved here.
          </AlertDescription>
        </Alert>
      )}

      <p className="text-sm text-muted-foreground">
        Per-month complimentary hour quotas for room types. Stored as
        plan_entitlements rows. Conference Room + Meeting Room synthetic items
        must exist in this space&apos;s inventory.
      </p>

      <div className="space-y-1.5">
        <Label>Conference Room Hours / month</Label>
        <Input
          type="number"
          min={0}
          step="1"
          value={value.confRoomHours}
          onChange={(e) => patch({ confRoomHours: e.target.value })}
          placeholder="e.g. 10"
          className="max-w-[200px]"
        />
      </div>

      <div className="space-y-1.5">
        <Label>Meeting Room Hours / month</Label>
        <Input
          type="number"
          min={0}
          step="1"
          value={value.meetRoomHours}
          onChange={(e) => patch({ meetRoomHours: e.target.value })}
          placeholder="e.g. 5"
          className="max-w-[200px]"
        />
      </div>

      <p className="text-[10px] text-muted-foreground">
        Quotas per month. Set 0 to omit. To manage all plan entitlements (other
        items, quotas, carry-forward) in detail, go to Inventory → Plans →
        Entitlements tab.
      </p>

      </fieldset>
      <div className="flex justify-end pt-2">
        {disabled ? (
          <Button
            type="button"
            variant="outline"
            onClick={onEdit}
            data-testid="plan-editor-credits-edit"
          >
            Edit
          </Button>
        ) : (
          <Button
          type="button"
          onClick={onSave}
          disabled={saving}
          data-testid="plan-editor-credits-save"
        >
          {saving ? "Saving…" : "Save & Continue"}
        </Button>
        )}
      </div>
    </div>
  );
}
